import { Handle, Position, useReactFlow } from '@xyflow/react';
import { formatDistanceToNow } from 'date-fns';
import { MessageSquare, BrainCircuit, UserCog, Settings, Plus, HelpCircle, Edit, Eye, Zap } from 'lucide-react';
import React, { memo, useState, useRef, KeyboardEvent, useEffect } from 'react';
import { getAgentRoleById, getAvailableAgentRoles } from '@/constants/agentRoles';
import { useUser } from '@/context/UserContext';
import useAgentManager, { createAgentProfileCommand, createDefaultAction } from '@/services/agentService';
import styles from '@/styles/NodeAgency.module.css';
import {
  Agent,
  AgentAction,
  AgentMessage,
  AgencyNodeData,
  SuggestedResponse,
  CreateAgentProfileCommand
} from '@/types/agentTypes';
import NodeBaseContainer from '../base/base-container/NodeBaseContainer';
import AgentActionEditor from '../../agency/AgentActionEditor'
import { AgentAvatar } from '../../agency/AgentAvatarSelector';
import AgentConfigurationDialog from '../../agency/AgentConfigurationDialog';
import CustomAgentCreationPanel from '../../agency/CustomAgentCreationPanel';
import NodeSelectionInterface from '../../agency/NodeSelectionInterface';
import SuggestedResponseButtons from '../../agency/SuggestedResponseButtons';

interface NodeAgencyProps {
  id: string;
  data: AgencyNodeData;
  selected: boolean;
}

const NodeAgency: React.FC<NodeAgencyProps> = ({ id, data, selected }) => {
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const user = useUser();
  const { updateNodeData } = useReactFlow();
  
  const [chatInput, setChatInput] = useState('');
  const [activeTab, setActiveTab] = useState('intellica');
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<AgentAction | null>(null);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [selectedNodeTypes, setSelectedNodeTypes] = useState<string[]>([]);
  const [showNodeSelection, setShowNodeSelection] = useState(false);
  
  // New state for enhanced agent creation
  const [agentCreationMode, setAgentCreationMode] = useState<'new' | 'fromRole' | 'customize'>('new');
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [customTitle, setCustomTitle] = useState<string>('');
  const [profileGenerationInProgress, setProfileGenerationInProgress] = useState(false);
  const [generatedProfile, setGeneratedProfile] = useState<any>(null);
  
  // Reference to track previous state updates
  const lastUpdateRef = useRef<number>(0);

  // Get the agent manager
  const agentManager = useAgentManager(id);
  
  // Destructure data properties
  const {
    isLocked = false,
    isMinimized = false,
    minimizeLevel = 'normal',
    assignedEmails = []
  } = data;

  // Get relevant data from node or use empty defaults
  const agents = data.agents || [];
  const intellicaMessages = data.intellicaMessages || [];
  
  // Ensure predefinedRoles are properly initialized
  const [predefinedRoles, setPredefinedRoles] = useState(() => {
    // Use the roles from the node data if available, otherwise get from constants
    return data.predefinedRoles || getAvailableAgentRoles();
  });
  
  // Initialize predefinedRoles in node data if not already present
  useEffect(() => {
    if (!data.predefinedRoles) {
      console.log("Initializing predefinedRoles in node data");
      updateNodeData(id, {
        ...data,
        predefinedRoles: getAvailableAgentRoles()
      });
    }
  }, [id, data, updateNodeData]);

  // Function handlers
  const handleLockChange = (isLocked: boolean) => {
    updateNodeData(id, { ...data, isLocked });
  };

  const handleMinimizeChange = (isMinimized: boolean) => {
    updateNodeData(id, { ...data, isMinimized });
  };

  const handleMinimizeLevelChange = (level: 'normal' | 'medium' | 'minimum') => {
    updateNodeData(id, { 
      ...data, 
      minimizeLevel: level,
      isMinimized: level !== 'normal'
    });
  };

  const handleTitleChange = (label: string) => {
    updateNodeData(id, { ...data, label });
  };

  const handleAssignUsersClick = () => {
    // Implement user assignment functionality
    // Assignment functionality to be implemented in future updates
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  // Scroll chat to bottom when new messages arrive
  React.useEffect(() => {
    if (messagesEndRef.current && activeTab === 'intellica') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [intellicaMessages, activeTab]);

  // Format timestamp to relative time
  const formatTimestamp = (timestamp: number): string => {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  };

  // Chat message submission handler for Intellica
  const handleSubmitMessage = (): void => {
    if (chatInput.trim()) {
      const newMessage = {
        id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        content: chatInput.trim(),
        email: user.email || 'anonymous',
        isIntelligenceResponse: false,
        timestamp: Date.now()
      };
      
      // Add message to the node's data
      const updatedMessages = [...(data.intellicaMessages || []), newMessage];
      
      // Create a command for the intellicaProcessor
      // The IntellicaProcessor expects the command in documentRequest with specific format
      const documentRequest = {
        document: 'intellica-goal-analysis',
        'sysagent-cmd': 'NodeIntellica-Process-Started',
        'sysagent-response': '',
        timestamp: Date.now(),
        processing: false,
        response: chatInput.trim(), // Use 'response' field, not 'message'
        [id]: { response: chatInput.trim() } // Add node-specific data
      };
      
      // Update the node data with the message and command
      updateNodeData(id, {
        ...data,
        intellicaMessages: updatedMessages,
        documentRequest: documentRequest
      });
      
      console.log("Sending command to Intellica processor:", documentRequest);
      
      // Clear the input field
      setChatInput('');
      
      // Check for response every second
      const checkInterval = setInterval(() => {
        const node = agentManager?.getAgencyNode();
        if (!node) {
          clearInterval(checkInterval);
          return;
        }
        
        const responseCommand = node.data.documentRequest;
        
        if (responseCommand && responseCommand['sysagent-response'] === 'NodeIntellica-Process-Finished') {
          clearInterval(checkInterval);
          console.log("Received response from Intellica processor:", responseCommand);
          
          // Parse the response from the processor
          try {
            const jsonMatch = responseCommand.response.match(/```json\s*([\s\S]*?)\s*```/);
            if (jsonMatch) {
              const jsonResponse = JSON.parse(jsonMatch[1]);
              
              // Create suggested responses from options if available
              const suggestedResponses = jsonResponse.options && jsonResponse.options.length > 0
                ? jsonResponse.options.map((opt: any) => ({
                    id: `sugg-${Date.now()}-${opt.value}`,
                    text: opt.label,
                    action: opt.value,
                    data: opt
                  }))
                : undefined;
              
              // Create a message from the actual AI response
              const intelligenceMessage = {
                id: `intel-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                content: jsonResponse.analysis,
                email: 'intellica@system.ai',
                isIntelligenceResponse: true,
                timestamp: Date.now(),
                suggestedResponses: suggestedResponses
              };
              
              // Add the intelligence response
              const updatedMessages = [...(data.intellicaMessages || []), intelligenceMessage];
              
              // Update the node data with the message and clear the command
              updateNodeData(id, {
                ...data,
                intellicaMessages: updatedMessages,
                documentRequest: null
              });
            } else {
              // Fallback if no JSON is found
              simulateIntelligenceResponse();
            }
          } catch (error) {
            console.error("Error parsing processor response:", error);
            simulateIntelligenceResponse();
          }
        }
      }, 1000);
    }
  };
  
  // Simulate intelligence response from Intellica
  const simulateIntelligenceResponse = (): void => {
    const responses = [
      "I've analyzed the data and found some interesting patterns. Would you like me to create a visualization?",
      "Based on the selected nodes, I can identify several key insights. Should I prepare a detailed report?",
      "I can help you optimize your workflow here. Would you like me to suggest some improvements?",
      "I notice you're working with structured data. Would you like me to create a summary document node?",
      "I can collaborate with your other agents to deliver a comprehensive solution. Just let me know what you need."
    ];
    
    const randomResponse = responses[Math.floor(Math.random() * responses.length)];
    
    const intelligenceMessage = {
      id: `intel-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      content: randomResponse,
      email: 'intellica@system.ai',
      isIntelligenceResponse: true,
      timestamp: Date.now()
    };
    
    // Add the intelligence response
    const updatedMessages = [...(data.intellicaMessages || []), intelligenceMessage];
    updateNodeData(id, { 
      ...data, 
      intellicaMessages: updatedMessages 
    });
  };

  // Handle Enter key in chat input
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitMessage();
    }
  };
// Add a new custom agent
const handleAddAgent = (): void => {
  // Reset state before opening dialog
  setSelectedAgent(null);
  setSelectedRoleId('custom'); // Set default role to 'custom'
  setCustomTitle('');
  setGeneratedProfile(null);
  setAgentCreationMode('new');
  
  // Open the dialog
  setConfigDialogOpen(true);
  
  console.log("Opening agent configuration dialog with role: custom");
};

// Add a new custom agent based on an existing role
const handleAddAgentFromRole = (roleId: string): void => {
  setSelectedAgent(null);
  setSelectedRoleId(roleId);
  setAgentCreationMode('fromRole');
  setConfigDialogOpen(true);
};

// Edit an existing agent
const handleEditAgent = (agent: Agent): void => {
  setSelectedAgent(agent);
  setAgentCreationMode('customize');
  setConfigDialogOpen(true);
};

// Generate an AI profile for an agent
const handleGenerateAgentProfile = (role: string, customizations?: any): void => {
  if (!agentManager) return;
  
  setProfileGenerationInProgress(true);
  
  try {
    // Create the command for the CustomAgentProcessor
    const command: CreateAgentProfileCommand = createAgentProfileCommand(role, customizations);
    
    console.log("Sending command to CustomAgentProcessor:", command);
    
    // Update the node data with the command
    updateNodeData(id, {
      ...data,
      agentCommand: command
    });
  } catch (error) {
    console.error("Error creating agent profile command:", error);
    setProfileGenerationInProgress(false);
    alert("Error creating agent profile: " + (error instanceof Error ? error.message : String(error)));
    return;
  }
  
  // Check for response every second
  const checkInterval = setInterval(() => {
    const node = agentManager.getAgencyNode();
    if (!node) return;
    
    const responseCommand = node.data.agentCommand;
    
    if (responseCommand && responseCommand['sysagent-response'] === 'CustomAgent-Create-Finished') {
      clearInterval(checkInterval);
      setProfileGenerationInProgress(false);
      setGeneratedProfile(responseCommand.agentProfile);
      
      // Clear the command from the node
      updateNodeData(id, {
        ...data,
        agentCommand: null
      });
    } else if (responseCommand && responseCommand['sysagent-response'] === 'CustomAgent-Create-Error') {
      clearInterval(checkInterval);
      setProfileGenerationInProgress(false);
      console.error('Error generating agent profile:', responseCommand.error);
      
      // Clear the command from the node
      updateNodeData(id, {
        ...data,
        agentCommand: null
      });
    }
  }, 1000);
};

// Save agent from configuration dialog
const handleSaveAgent = (agent: Agent): void => {
  if (agentManager) {
    try {
      console.log("handleSaveAgent called with agent:", agent);
      
      if (selectedAgent) {
        console.log("Updating existing agent:", agent);
        // Update existing agent
        agentManager.updateAgent(agent.id, agent);
      } else {
        // Add new agent
        if (generatedProfile) {
          console.log("Using generated profile:", generatedProfile);
          
          // Create a safe copy of the agent to modify
          const enhancedAgent = {
            ...agent,
            description: generatedProfile.description || agent.description,
            capabilities: generatedProfile.capabilities || [],
            // Ensure monitoredNodes is initialized
            monitoredNodes: agent.monitoredNodes || { nodeIds: [], nodeTypes: [], monitorAll: false }
          };
          
          // Add suggested actions if available
          if (generatedProfile.suggestedActions && generatedProfile.suggestedActions.length > 0) {
            console.log("Adding suggested actions:", generatedProfile.suggestedActions);
            enhancedAgent.actions = generatedProfile.suggestedActions.map((action: any) =>
              createDefaultAction(
                action.name,
                action.description,
                action.prompt,
                'manual'
              )
            );
          }
          
          console.log("Adding enhanced agent:", enhancedAgent);
          agentManager.addAgent(enhancedAgent);
        } else {
          // Add regular agent with initialized monitoredNodes
          const safeAgent = {
            ...agent,
            // Ensure monitoredNodes is initialized
            monitoredNodes: agent.monitoredNodes || { nodeIds: [], nodeTypes: [], monitorAll: false }
          };
          
          console.log("Adding regular agent:", safeAgent);
          agentManager.addAgent(safeAgent);
        }
      }
      
      // Reset state
      setGeneratedProfile(null);
      setCustomTitle('');
      setConfigDialogOpen(false); // Ensure dialog is closed
      
      console.log("Agent saved successfully");
    } catch (error) {
      console.error("Error saving agent:", error);
      alert("Error saving agent: " + (error instanceof Error ? error.message : String(error)));
    }
  } else {
    console.error("Agent manager not available");
    alert("Error: Agent manager not available");
  }
};

// Remove an agent
const handleRemoveAgent = (agentId: string): void => {
  if (agentManager && confirm('Are you sure you want to remove this agent?')) {
    agentManager.removeAgent(agentId);
  }
};

// Toggle agent enabled state
const handleToggleAgentEnabled = (agentId: string, isEnabled: boolean): void => {
  if (agentManager) {
    agentManager.toggleAgentEnabled(agentId, isEnabled);
  }
};

// Add a new action to an agent
const handleAddAction = (agent: Agent): void => {
  setSelectedAgent(agent);
  setSelectedAction(null);
  setActionDialogOpen(true);
};

// Edit an existing action
const handleEditAction = (agent: Agent, action: AgentAction): void => {
  setSelectedAgent(agent);
  setSelectedAction(action);
  setActionDialogOpen(true);
};

// Save action from action editor
const handleSaveAction = (action: AgentAction): void => {
  if (agentManager && selectedAgent) {
    if (selectedAction) {
      // Update existing action
      const updatedActions = selectedAgent.actions.map(a =>
        a.id === action.id ? action : a
      );
      agentManager.updateAgent(selectedAgent.id, {
        ...selectedAgent,
        actions: updatedActions
      });
    } else {
      // Add new action
      agentManager.addAgentAction(selectedAgent.id, action);
    }
  }
};

// Configure agent monitoring
const handleConfigureMonitoring = (agent: Agent): void => {
  setSelectedAgent(agent);
  // Ensure monitoredNodes exists before accessing its properties
  setSelectedNodeIds((agent.monitoredNodes?.nodeIds) || []);
  setSelectedNodeTypes((agent.monitoredNodes?.nodeTypes) || []);
  setShowNodeSelection(true);
};

// Save node selection for monitoring
const handleSaveNodeSelection = (): void => {
  if (agentManager && selectedAgent) {
    agentManager.updateAgentMonitoring(selectedAgent.id, {
      nodeIds: selectedNodeIds,
      nodeTypes: selectedNodeTypes,
      monitorAll: false
    });
    setShowNodeSelection(false);
  }
};

// Handle suggested response click
const handleSuggestedResponseClick = (suggestion: SuggestedResponse): void => {
  // Handle the suggestion based on its action
  
  // Add user message with the suggestion text
  if (activeTab === 'intellica') {
    const newMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      content: suggestion.text,
      email: user.email || 'anonymous',
      isAgentResponse: false,
      timestamp: Date.now()
    };
    
    // Add message to the node's data
    const updatedMessages = [...(data.intellicaMessages || []), newMessage];
    
    // Create a command for the intellicaProcessor with the correct format
    const documentRequest = {
      document: 'intellica-goal-analysis',
      'sysagent-cmd': 'NodeIntellica-Process-Started',
      'sysagent-response': '',
      timestamp: Date.now(),
      processing: false,
      response: suggestion.text, // Use 'response' field, not 'message'
      [id]: { response: suggestion.text } // Add node-specific data
    };
    
    // Update the node data with the message and command
    updateNodeData(id, {
      ...data,
      intellicaMessages: updatedMessages,
      documentRequest: documentRequest
    });
    
    console.log("Sending suggested response to Intellica processor:", documentRequest);
    
    // Start checking for response
    const checkInterval = setInterval(() => {
      const node = agentManager?.getAgencyNode();
      if (!node) {
        clearInterval(checkInterval);
        return;
      }
      
      const responseCommand = node.data.documentRequest;
      
      if (responseCommand && responseCommand['sysagent-response'] === 'NodeIntellica-Process-Finished') {
        clearInterval(checkInterval);
        console.log("Received response from Intellica processor:", responseCommand);
        
        // Parse the response from the processor
        try {
          const jsonMatch = responseCommand.response.match(/```json\s*([\s\S]*?)\s*```/);
          if (jsonMatch) {
            const jsonResponse = JSON.parse(jsonMatch[1]);
            
            // Create suggested responses from options if available
            const suggestedResponses = jsonResponse.options && jsonResponse.options.length > 0
              ? jsonResponse.options.map((opt: any) => ({
                  id: `sugg-${Date.now()}-${opt.value}`,
                  text: opt.label,
                  action: opt.value,
                  data: opt
                }))
              : undefined;
            
            // Create a message from the actual AI response
            const intelligenceMessage = {
              id: `intel-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
              content: jsonResponse.analysis,
              email: 'intellica@system.ai',
              isIntelligenceResponse: true,
              timestamp: Date.now(),
              suggestedResponses: suggestedResponses
            };
            
            // Add the intelligence response
            const updatedMessages = [...(data.intellicaMessages || []), intelligenceMessage];
            
            // Update the node data with the message and clear the command
            updateNodeData(id, {
              ...data,
              intellicaMessages: updatedMessages,
              documentRequest: null
            });
          } else {
            // Fallback if no JSON is found
            simulateIntelligenceResponse();
          }
        } catch (error) {
          console.error("Error parsing processor response:", error);
          simulateIntelligenceResponse();
        }
      }
    }, 1000);
  } else if (selectedAgent) {
    const newMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      content: suggestion.text,
      email: user.email || 'anonymous',
      isAgentResponse: false,
      timestamp: Date.now()
    };
    
    if (agentManager && selectedAgent) {
      agentManager.addAgentMessage(selectedAgent.id, newMessage);
    }
    
    // Simulate agent response
    setTimeout(() => {
      simulateAgentResponse(selectedAgent);
    }, 1000 + Math.random() * 2000);
  }
};
  

  // Simulate agent response
  const simulateAgentResponse = (agent: Agent): void => {
    const responses = [
      `As your ${agent.name}, I've analyzed the selected nodes and found some interesting patterns.`,
      `Based on my role as ${agent.name}, I can help optimize your workflow here.`,
      `I've examined the content and have several recommendations for improvements.`,
      `I can assist with further analysis of your project data. What specific insights are you looking for?`
    ];
    
    const randomResponse = responses[Math.floor(Math.random() * responses.length)];
    
    const suggestedResponses: SuggestedResponse[] = [
      {
        id: `sugg-${Date.now()}-1`,
        text: 'Tell me more about your findings',
        action: 'expand'
      },
      {
        id: `sugg-${Date.now()}-2`,
        text: 'Generate a detailed report',
        action: 'report'
      },
      {
        id: `sugg-${Date.now()}-3`,
        text: 'Suggest specific improvements',
        action: 'improve'
      }
    ];
    
    const agentMessage: AgentMessage = {
      id: `agent-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      content: randomResponse,
      email: 'agent@system.ai',
      isAgentResponse: true,
      timestamp: Date.now(),
      suggestedResponses
    };
    
    if (agentManager && agent) {
      agentManager.addAgentMessage(agent.id, agentMessage);
    }
  };
  
  // Custom buttons for the node header
  const customButtons = [
    {
      id: '1',
      onClick: () => handleAddAgent(),
      icon: <Plus size={16} />,
      tooltip: 'Add custom agent',
    },
    {
      id: '2',
      onClick: () => {}, // TODO: Implement agency settings
      icon: <Settings size={16} />,
      tooltip: 'Settings',
    },
    {
      id: '3',
      onClick: () => {}, // TODO: Implement help guide
      icon: <HelpCircle size={16} />,
      tooltip: 'Help & Guide',
    },
  ];

  // Get the node system state
  // Get the node system state
  const systemState = data?.systemState || { lastCheck: Date.now(), isHealthy: true };

  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {};
    
    if (agents && agents.length > 0) {
      minifiedInfo.agents = `${agents.length} agent${agents.length !== 1 ? 's' : ''}`;
      
      const enabledAgents = agents.filter(agent => agent.isEnabled);
      if (enabledAgents.length !== agents.length) {
        minifiedInfo.enabled = `${enabledAgents.length} enabled`;
      }
    }
    
    if (intellicaMessages && intellicaMessages.length > 0) {
      minifiedInfo.messages = `${intellicaMessages.length} message${intellicaMessages.length !== 1 ? 's' : ''}`;
    }
    
    if (systemState && !systemState.isHealthy) {
      minifiedInfo.status = 'System issues detected';
    }
    
    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {};
    const hasChanges = JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo);
    
    if (hasChanges) {
      updateNodeData(id, { ...data, minifiedInfo });
    }
  }, [agents, intellicaMessages, systemState, data.minifiedInfo, id, data]);

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={true}
      nodeTitle={data.label || "Project Agency"}
      isLocked={isLocked}
      isMinimized={isMinimized}
      minimizeLevel={minimizeLevel}
      onLockChange={handleLockChange}
      onMinimizeChange={handleMinimizeChange}
      onMinimizeLevelChange={handleMinimizeLevelChange}
      onTitleChange={handleTitleChange}
      customButtons={customButtons}
      assignedEmails={assignedEmails}
      onAssignUsersClick={handleAssignUsersClick}
      selected={selected}
    >
      {minimizeLevel === 'normal' && (
        <div className={styles['agency-node']}>
          {/* System State Indicator */}
          <div className={styles['system-state']}>
            <div 
              className={`${styles['system-state-indicator']} ${
                systemState.isHealthy 
                  ? styles['system-state-healthy'] 
                  : styles['system-state-unhealthy']
              }`}
            />
            <span>
              {agents.length} active agent{agents.length !== 1 ? 's' : ''} {' • '} 
              Last check: {formatTimestamp(systemState.lastCheck)}
            </span>
          </div>

          {/* Tab Navigation */}
          <div className={styles['tab-navigation']}>
            <button 
              className={`${styles['tab-button']} ${activeTab === 'intellica' ? styles.active : ''}`}
              onClick={() => handleTabChange('intellica')}
            >
              <BrainCircuit size={16} className="mr-1" />
              Intellica
            </button>
            <button 
              className={`${styles['tab-button']} ${activeTab === 'customAgents' ? styles.active : ''}`}
              onClick={() => handleTabChange('customAgents')}
            >
              <UserCog size={16} className="mr-1" />
              Custom Agents
            </button>
          </div>

          {/* Tab Content */}
          <div className={styles['node-content']}>
            {/* Intellica Tab */}
            {activeTab === 'intellica' && (
              <div className={styles['chat-container']}>
                <div className={styles['messages-container']}>
                  {intellicaMessages.length === 0 ? (
                    <div className="text-center py-4">
                      <div className="bg-violet-50 p-4 rounded-lg text-violet-800 mb-4">
                        <h3 className="font-medium">Welcome to Intellica</h3>
                        <p className="text-sm">
                          I'm your lead intelligence agent. Ask me to analyze data, create nodes, 
                          generate reports, or help manage your project.
                        </p>
                      </div>
                      <p className="text-gray-500">Start a conversation below to begin!</p>
                    </div>
                  ) : (
                    intellicaMessages.map((message: any) => (
                      <div
                        key={message.id}
                        className={`${styles.message} ${
                          message.isIntelligenceResponse
                            ? styles['message-intelligence']
                            : styles['message-self']
                        }`}
                      >
                        <div className="text-xs font-medium mb-1">
                          {message.isIntelligenceResponse ? 'Intellica' : 'You'}
                          {' • '}
                          {formatTimestamp(message.timestamp)}
                        </div>
                        <div>{message.content}</div>
                        
                        {/* Display suggested responses if available */}
                        {message.suggestedResponses && message.suggestedResponses.length > 0 && (
                          <SuggestedResponseButtons
                            suggestions={message.suggestedResponses}
                            onSelect={handleSuggestedResponseClick}
                            className="mt-2"
                          />
                        )}
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
                <div className={styles['chat-input-container']}>
                  <textarea
                    ref={chatInputRef}
                    className={styles['chat-input']}
                    placeholder="Ask Intellica something..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLocked}
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* Custom Agents Tab */}
            {activeTab === 'customAgents' && (
              <div className={styles['agents-container']}>
                {!agents || agents.length === 0 ? (
                  <div className="p-4">
                    <p className="text-gray-500 mb-3 text-center">No custom agents yet.</p>
                    <CustomAgentCreationPanel
                      roles={predefinedRoles}
                      onCreateAgent={(name, description, roleId) => {
                        // Create agent directly without opening dialog
                        if (agentManager) {
                          try {
                            console.log(`Creating agent with name: ${name}, description: ${description}, role: ${roleId}`);
                            
                            // Create the agent using the service
                            const newAgent = {
                              id: `agent-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                              name: name,
                              description: description,
                              role: roleId,
                              isActive: true,
                              isEnabled: true,
                              isCustom: true,
                              avatarImage: getAgentRoleById(roleId)?.avatarOptions[0] || '/images/avatars/custom-agent-1.png',
                              monitoredNodes: {
                                nodeIds: [],
                                nodeTypes: getAgentRoleById(roleId)?.defaultNodeTypes || [],
                                monitorAll: false
                              },
                              createdNodeTypes: getAgentRoleById(roleId)?.defaultNodeTypes || [],
                              actions: [],
                              lastActivity: Date.now(),
                              messages: [],
                              settings: {
                                notifyOnChanges: true,
                                autoExecuteActions: false,
                                priorityLevel: 'medium',
                                triggerThreshold: 'significant',
                                triggerDelay: 5,
                                checkSchedule: 'none'
                              }
                            };
                            
                            // Add the agent directly to the node data
                            const updatedAgents = [...agents, newAgent];
                            updateNodeData(id, {
                              ...data,
                              agents: updatedAgents
                            });
                            
                            console.log("Agent created successfully:", newAgent);
                          } catch (error) {
                            console.error("Error creating agent:", error);
                            alert("Error creating agent: " + (error instanceof Error ? error.message : String(error)));
                          }
                        }
                      }}
                      onCreateFromRole={handleAddAgentFromRole}
                      onGenerateProfile={(roleId, customizations) => {
                        setSelectedRoleId(roleId);
                        handleGenerateAgentProfile(roleId, customizations);
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <div className="mb-3 flex justify-between items-center">
                      <h3 className="font-medium">Your Agents</h3>
                      <button
                        onClick={handleAddAgent}
                        disabled={isLocked}
                        className="text-sm bg-violet-500 text-white py-1 px-2 rounded-md hover:bg-violet-600 transition-colors"
                      >
                        <Plus size={12} className="inline" /> Add
                      </button>
                    </div>
                    <div className={styles['agents-list']}>
                      {agents.map((agent: Agent) => (
                        <div key={agent.id} className={styles['agent-item']}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-2">
                              <AgentAvatar
                                avatarUrl={agent.avatarImage}
                                name={agent.name}
                                size="sm"
                                status={agent.isEnabled ? 'online' : 'offline'}
                              />
                              <div>
                                <h4 className="font-medium text-violet-700">{agent.name}</h4>
                                <p className="text-xs text-gray-600">{agent.description}</p>
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {agent.role && getAgentRoleById(agent.role)?.capabilities?.map((cap: string, i: number) => (
                                    <span key={i} className="text-xs bg-violet-100 text-violet-800 px-1 rounded">
                                      {cap}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <div className="flex items-center">
                                <div className={`h-2 w-2 rounded-full ${agent.isEnabled ? 'bg-green-500' : 'bg-gray-400'} mr-1`}></div>
                                <span className="text-xs">{agent.isEnabled ? 'Enabled' : 'Disabled'}</span>
                              </div>
                              <div className="flex gap-1">
                                <button
                                  className="text-xs p-1 text-blue-600 hover:bg-blue-50 rounded"
                                  onClick={() => handleEditAgent(agent)}
                                  title="Edit agent"
                                >
                                  <Edit size={12} />
                                </button>
                                <button
                                  className="text-xs p-1 text-green-600 hover:bg-green-50 rounded"
                                  onClick={() => handleConfigureMonitoring(agent)}
                                  title="Configure monitoring"
                                >
                                  <Eye size={12} />
                                </button>
                                <button
                                  className="text-xs p-1 text-amber-600 hover:bg-amber-50 rounded"
                                  onClick={() => handleAddAction(agent)}
                                  title="Add action"
                                >
                                  <Zap size={12} />
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="mt-2">
                            <div className="text-xs font-medium mb-1">Actions:</div>
                            <div className="flex flex-wrap gap-1">
                              {agent.actions && agent.actions.length > 0 ? (
                                agent.actions.map(action => (
                                  <button
                                    key={action.id}
                                    className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100"
                                    onClick={() => handleEditAction(agent, action)}
                                  >
                                    {action.name}
                                  </button>
                                ))
                              ) : (
                                <span className="text-xs text-gray-500">No actions configured</span>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 flex justify-between items-center">
                            <span className="text-xs text-gray-500">
                              {formatTimestamp(agent.lastActivity)}
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                className="text-xs text-violet-600 flex items-center"
                                onClick={() => setActiveTab(agent.id)}
                              >
                                <MessageSquare size={12} className="mr-1" />
                                {agent.messages?.length || 0}
                              </button>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={agent.isEnabled}
                                  onChange={(e) => handleToggleAgentEnabled(agent.id, e.target.checked)}
                                  className="sr-only"
                                />
                                <div className={`w-8 h-4 rounded-full transition-colors ${agent.isEnabled ? 'bg-green-400' : 'bg-gray-300'}`}>
                                  <div className={`w-3 h-3 rounded-full bg-white transform transition-transform ${agent.isEnabled ? 'translate-x-4' : 'translate-x-1'}`}></div>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Agent Chat Tab (for individual agents) */}
            {activeTab !== 'intellica' && activeTab !== 'customAgents' && (
              <div className={styles['chat-container']}>
                {agents.map((agent: Agent) => {
                  if (agent.id !== activeTab) return null;
                  
                  return (
                    <div key={agent.id} className="flex flex-col h-full">
                      <div className="flex items-center gap-2 p-2 border-b">
                        <button
                          className="text-gray-500 hover:text-gray-700"
                          onClick={() => setActiveTab('customAgents')}
                        >
                          &larr; Back
                        </button>
                        <AgentAvatar
                          avatarUrl={agent.avatarImage}
                          name={agent.name}
                          size="sm"
                          status={agent.isEnabled ? 'online' : 'offline'}
                        />
                        <div>
                          <div className="font-medium">{agent.name}</div>
                          <div className="text-xs text-gray-500">
                            {getAgentRoleById(agent.role)?.name || agent.role}
                          </div>
                        </div>
                      </div>
                      
                      <div className={styles['messages-container']}>
                        {(!agent.messages || agent.messages.length === 0) ? (
                          <div className="text-center py-4">
                            <div className="bg-violet-50 p-4 rounded-lg text-violet-800 mb-4">
                              <h3 className="font-medium">Chat with {agent.name}</h3>
                              <p className="text-sm">
                                This agent can help you with tasks related to its role as {getAgentRoleById(agent.role)?.name || agent.role}.
                              </p>
                            </div>
                            <p className="text-gray-500">Start a conversation below to begin!</p>
                          </div>
                        ) : (
                          agent.messages.map((message: AgentMessage) => (
                            <div
                              key={message.id}
                              className={`${styles.message} ${
                                message.isAgentResponse
                                  ? styles['message-intelligence']
                                  : styles['message-self']
                              }`}
                            >
                              <div className="text-xs font-medium mb-1">
                                {message.isAgentResponse ? agent.name : 'You'}
                                {' • '}
                                {formatTimestamp(message.timestamp)}
                              </div>
                              <div>{message.content}</div>
                              
                              {message.suggestedResponses && message.suggestedResponses.length > 0 && (
                                <SuggestedResponseButtons
                                  suggestions={message.suggestedResponses}
                                  onSelect={handleSuggestedResponseClick}
                                  className="mt-2"
                                />
                              )}
                            </div>
                          ))
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                      
                      <div className={styles['chat-input-container']}>
                        <textarea
                          ref={chatInputRef}
                          className={styles['chat-input']}
                          placeholder={`Ask ${agent.name} something...`}
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={handleKeyDown}
                          disabled={isLocked || !agent.isEnabled}
                          rows={2}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Connection Handles */}
      <Handle type="target" position={Position.Top} />
      <Handle type="source" position={Position.Bottom} />
      
      {/* Dialogs */}
      <AgentConfigurationDialog
        open={configDialogOpen}
        onOpenChange={setConfigDialogOpen}
        onSave={handleSaveAgent}
        existingAgent={selectedAgent || undefined}
        creationMode={agentCreationMode}
        initialRoleId={selectedRoleId}
        onGenerateProfile={handleGenerateAgentProfile}
        generatedProfile={generatedProfile}
        profileGenerationInProgress={profileGenerationInProgress}
      />
      
      <AgentActionEditor
        open={actionDialogOpen}
        onOpenChange={setActionDialogOpen}
        onSave={handleSaveAction}
        existingAction={selectedAction || undefined}
      />
      
      {showNodeSelection && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium mb-4">Configure Monitoring for {selectedAgent.name}</h3>
            
            <NodeSelectionInterface
              selectedNodeIds={selectedNodeIds}
              selectedNodeTypes={selectedNodeTypes}
              onNodeSelectionChange={setSelectedNodeIds}
              onNodeTypeSelectionChange={setSelectedNodeTypes}
            />
            
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowNodeSelection(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNodeSelection}
                className="px-4 py-2 bg-violet-500 text-white rounded-md hover:bg-violet-600"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </NodeBaseContainer>
  );
};

// Export the component as default
export default memo(NodeAgency);

